<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import useToast from "@/ui/composables/useToast";
import { useBasePriceStore } from "@/modules/base_price/store";
import PlanPreviousYearsAverageChart from "@/modules/base_price/components/PlanPreviousYearsAverageChart.vue";
import BasePricePreviousYearAverageDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearAverageDTO;

/**
 * "Derive CPM from previous years": loads the average CPM per month the node
 * or screen being edited had in earlier years, to price this plan's months
 * after. The CPM counterpart of PlanPreviousYearsDialog — fetched every time
 * it opens, since the selection may have moved in between.
 */
const props = defineProps<{
	/** The node or screen the tab is editing, for the dialog's subtitle. */
	targetName: string;
}>();

const visible = defineModel<boolean>("visible", { default: false });

const emit = defineEmits<{
	/** The twelve months' CPM to set, January first; 0 where a year has no CPM for that month. */
	apply: [cpms: number[]];
}>();

const store = useBasePriceStore();

/** The years the figures are for: the two before the plan's (see PreviousYearDistributionService). */
const currentYear = computed(() => (store.plan?.year ?? 0) - 1);
const previousYear = computed(() => (store.plan?.year ?? 0) - 2);

const loading = ref(false);
const averages = ref<BasePricePreviousYearAverageDTO | null>(null);

/** The twelve months, normalized out of the keyed-object-or-array shape a Collection serializes to. */
const months = computed(() =>
	averages.value?.months ? Object.values(averages.value.months) : []
);

/**
 * Only the latest request may fill the dialog: the explorer can take long
 * enough that reopening it — for another target, say — overtakes the
 * previous request, and its answer must not land on top.
 */
let latestRequest = 0;

const load = async () => {
	const request = ++latestRequest;

	loading.value = true;
	averages.value = null;

	const response = await store.fetchPreviousYearsCpm();

	if (request !== latestRequest) return;

	loading.value = false;

	if (response?.isErr()) {
		useToast().error(response.error.message);

		return;
	}

	averages.value = response?.isOk() ? response.value : null;
};

watch(visible, (open) => open && load());

/**
 * The year before the plan can be applied as soon as every month has a CPM —
 * a month still running counts too: what it has sold so far is already a
 * price. Without one, applying it would leave that month unpriced.
 */
const currentYearComplete = computed(
	() => months.value.length > 0 && months.value.every((month) => month.current_year > 0)
);

const apply = (key: "current_year" | "previous_year" | "average") => {
	emit(
		"apply",
		months.value.map((month) => month[key] ?? 0)
	);
	visible.value = false;
};
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Derive CPM from previous years"
		icon="fa-light fa-clock-rotate-left"
		width="56rem"
		:persistent="false"
	>
		<div v-if="loading" class="d-flex flex-column align-center ga-3 py-8">
			<v-progress-circular indeterminate color="primary" size="40" />
			<span class="text-caption text-medium-emphasis">Loading previous years…</span>
		</div>

		<div
			v-else-if="!months.length"
			class="text-body-2 text-medium-emphasis text-center py-8"
		>
			No previous-year data found for {{ props.targetName }}.
		</div>

		<template v-else>
			<!-- Whose years these are: the node or screen the tab was editing when it opened. -->
			<div class="d-flex align-baseline ga-2 mb-1">
				<span class="text-subtitle-1 font-weight-bold">{{ props.targetName }}</span>
				<span v-if="store.editTarget" class="text-caption text-medium-emphasis">
					{{ store.editTarget.kindLabel }}
				</span>
			</div>

			<p class="text-caption text-medium-emphasis mb-3">
				Each line shows a month's average CPM. For a month of {{ currentYear }}
				that has not fully passed, its CPM is what has been sold so far. The
				average is the mean of {{ currentYear }} and {{ previousYear }} for a
				month that has passed; for any other month it takes {{ previousYear }}'s
				CPM, adjusted by how {{ currentYear }}'s CPM is trending against
				{{ previousYear }}'s over the months that have passed. Applying sets each
				month's CPM by hand; with Hold budget on, its fill follows so the month
				stays on budget.
			</p>

			<plan-previous-years-average-chart
				:months="months"
				:current-year="currentYear"
				:previous-year="previousYear"
				format="money"
			/>
		</template>

		<template v-slot:actions>
			<div class="mx-5">Apply:</div>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !currentYearComplete"
				@click="apply('current_year')"
			>
				{{ currentYear }}
			</v-btn>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !months.length"
				@click="apply('previous_year')"
			>
				{{ previousYear }}
			</v-btn>
			<v-btn
				class="flex-1-1"
				variant="tonal"
				:disabled="loading || !months.length"
				@click="apply('average')"
			>
				Average
			</v-btn>
		</template>
	</modal>
</template>
